/**
 * Phase 4 — verify bootstrap output and write bootstrap-report.json.
 */

import { mkdir, writeFile } from "node:fs/promises";
import { join } from "node:path";
import type { ProbeResult } from "./probe.js";

export interface VerifyCheck {
  success: boolean;
  error?: string;
}

export interface BootstrapReport {
  bootstrap_at: string;
  mode: "full" | "artifacts-only";
  no_llm: boolean;
  probe: ProbeResult;
  generated: string[];
  verify: {
    loadProjectConfig?: VerifyCheck;
  };
}

export interface VerifyResult {
  report: BootstrapReport;
  reportPath: string;
}

export async function verifyBootstrap(
  projectRoot: string,
  projectYamlPath: string,
  probe: ProbeResult,
  options: {
    mode: "full" | "artifacts-only";
    noLlm: boolean;
    generated: string[];
  },
): Promise<VerifyResult> {
  const verify: BootstrapReport["verify"] = {};

  try {
    const runtime = await import("@aaac/runtime/generator");
    await runtime.loadProjectConfig(projectYamlPath);
    verify.loadProjectConfig = { success: true };
  } catch (err) {
    verify.loadProjectConfig = {
      success: false,
      error: err instanceof Error ? err.message : String(err),
    };
  }

  const report: BootstrapReport = {
    bootstrap_at: new Date().toISOString(),
    mode: options.mode,
    no_llm: options.noLlm,
    probe,
    generated: options.generated,
    verify,
  };

  const aaacDir = join(projectRoot, ".aaac");
  await mkdir(aaacDir, { recursive: true });
  const reportPath = join(aaacDir, "bootstrap-report.json");
  await writeFile(reportPath, JSON.stringify(report, null, 2) + "\n", "utf8");

  return { report, reportPath: ".aaac/bootstrap-report.json" };
}
